import { useEffect, useState } from 'react';
import type { BackgroundJob } from './blynChatService';
import { humaniseSkill } from './blynChatService';
import { watchJobProgress } from './backgroundJobProgressStream';

/** App-wide registry of background builds (journey / campaign creates) being
 * followed past the end of their chat turn. Lives outside any page so a watch
 * survives switching conversation or route, and raises a notice when a job
 * finishes while the user isn't looking at its message. */

export type TrackedJobState = 'running' | 'success' | 'error' | 'warning';

export interface TrackedJob {
  key: string;
  job: BackgroundJob;
  skill: string;
  /** Where the job's step row lives. `messageIndex` is the anchor that
   * survives a reload from history, which assigns messages new ids. */
  conversationId: string;
  messageId: string;
  messageIndex: number;
  stepId: string;
  state: TrackedJobState;
  label: string;
  detail?: string;
  /** 0–100 while running, when the worker reports it. */
  percent?: number;
}

export interface JobNotice {
  id: string;
  kind: 'success' | 'error' | 'warning';
  title: string;
  detail?: string;
  conversationId: string;
  messageIndex: number;
}

type Listener<T> = (value: T) => void;

export const backgroundJobKey = (job: BackgroundJob) => `${job.kind}:${job.id}`;

// Finished jobs stay in the map so a late duplicate report of the same job
// (tool_call(done) and skill_done both carry it) is ignored rather than
// reopening the stream.
const jobs = new Map<string, TrackedJob>();
const stops = new Map<string, () => void>();
const jobListeners = new Set<Listener<TrackedJob>>();
let notices: JobNotice[] = [];
const noticeListeners = new Set<Listener<JobNotice[]>>();
let visibilityProbe: ((job: TrackedJob) => boolean) | null = null;
type FocusTarget = { conversationId: string; messageIndex: number };
let pendingFocus: FocusTarget | null = null;
const focusListeners = new Set<Listener<FocusTarget>>();

function update(key: string, patch: Partial<TrackedJob>) {
  const current = jobs.get(key);
  if (!current) return;
  const next = { ...current, ...patch };
  jobs.set(key, next);
  jobListeners.forEach((fn) => fn(next));
}

function settle(key: string, state: Exclude<TrackedJobState, 'running'>, label: string, detail?: string) {
  stops.get(key)?.();
  stops.delete(key);
  update(key, { state, label, detail });
  const job = jobs.get(key);
  if (!job) return;
  // Looking at the message already — the step row updating is enough.
  const onScreen = typeof document !== 'undefined' && document.visibilityState === 'visible' && !!visibilityProbe?.(job);
  if (onScreen) return;
  pushNotice({
    id: `${key}:${Date.now()}`,
    kind: state,
    title: label,
    detail,
    conversationId: job.conversationId,
    messageIndex: job.messageIndex,
  });
}

function pushNotice(notice: JobNotice) {
  notices = [...notices, notice];
  noticeListeners.forEach((fn) => fn(notices));
}

export function dismissJobNotice(id: string) {
  notices = notices.filter((n) => n.id !== id);
  noticeListeners.forEach((fn) => fn(notices));
}

/** Start following a pending build. Returns false if this job is already
 * (or was already) tracked. */
export function trackBackgroundJob(input: {
  job: BackgroundJob;
  skill: string;
  conversationId: string;
  messageId: string;
  messageIndex: number;
  stepId: string;
  label: string;
  detail?: string;
  tenantId?: string;
}): boolean {
  const key = backgroundJobKey(input.job);
  if (jobs.has(key)) return false;
  const { tenantId, ...rest } = input;
  jobs.set(key, { ...rest, key, state: 'running' });
  jobListeners.forEach((fn) => fn(jobs.get(key)!));

  const noun = humaniseSkill(input.skill);
  const stop = watchJobProgress(input.job, {
    tenantId,
    onUpdate: (u) => {
      if (u.terminal === 'success') settle(key, 'success', `${noun} created`, u.message);
      else if (u.terminal === 'error') settle(key, 'error', `${noun} — couldn't complete`, u.message || `The ${noun.toLowerCase()} build failed.`);
      else if (u.message || u.percent != null) {
        update(key, { ...(u.message && { detail: u.message }), ...(u.percent != null && { percent: u.percent }) });
      }
    },
    onLost: () =>
      settle(key, 'warning', jobs.get(key)?.label || noun, `Lost track of progress — check the ${noun}s page for its status.`),
  });
  stops.set(key, stop);
  return true;
}

/** The chat turn's conversation id can be assigned by the server after the
 * job was picked up — keep the job pointing at the real conversation. */
export function setBackgroundJobConversation(messageId: string, conversationId: string) {
  jobs.forEach((job) => {
    if (job.messageId === messageId && job.conversationId !== conversationId) update(job.key, { conversationId });
  });
}

export function getBackgroundJobs(conversationId: string): TrackedJob[] {
  return [...jobs.values()].filter((j) => j.conversationId === conversationId);
}

/** Live list of the jobs tracked for one conversation, oldest first. */
export function useBackgroundJobs(conversationId: string): TrackedJob[] {
  const [list, setList] = useState(() => getBackgroundJobs(conversationId));
  useEffect(() => {
    setList(getBackgroundJobs(conversationId));
    return subscribeBackgroundJobs(() => setList(getBackgroundJobs(conversationId)));
  }, [conversationId]);
  return list;
}

export function subscribeBackgroundJobs(fn: Listener<TrackedJob>): () => void {
  jobListeners.add(fn);
  return () => { jobListeners.delete(fn); };
}

export function subscribeJobNotices(fn: Listener<JobNotice[]>): () => void {
  noticeListeners.add(fn);
  fn(notices);
  return () => { noticeListeners.delete(fn); };
}

/** The conversation page registers how to tell whether a job's message is on
 * screen; with none registered (another route is showing) nothing is. */
export function setJobVisibilityProbe(probe: ((job: TrackedJob) => boolean) | null) {
  visibilityProbe = probe;
}

/** Ask the conversation page to scroll to a message once it's showing;
 * the page claims it with takeJobFocus. */
export function requestJobFocus(target: FocusTarget) {
  pendingFocus = target;
  focusListeners.forEach((fn) => fn(target));
}

export function takeJobFocus(conversationId: string): number | null {
  if (!pendingFocus || pendingFocus.conversationId !== conversationId) return null;
  const { messageIndex } = pendingFocus;
  pendingFocus = null;
  return messageIndex;
}

export function subscribeJobFocus(fn: Listener<FocusTarget>): () => void {
  focusListeners.add(fn);
  return () => { focusListeners.delete(fn); };
}

/** Close every progress connection and forget all jobs — called when the
 * Blyn app itself unmounts. */
export function stopAllBackgroundJobs() {
  stops.forEach((stop) => stop());
  stops.clear();
  jobs.clear();
  notices = [];
  pendingFocus = null;
  noticeListeners.forEach((fn) => fn(notices));
}
